import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import Notification from "application/components/Notification";
import AuthenticationService from "../AuthenticationService";
// Authentication layout components
import IllustrationLayout from "./layout";
import { CircularProgress, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  useMaterialUIController,
  setActivePage,
  setAuthentication,
  setLayout,
  setUserName,
  setPassWord,
} from "context";
import { externalCallServer } from "globals/endpoints";
import CryptoJS from "crypto-js"; // Import the crypto-js library for encryption
import { application } from "globals/endpoints";
import CTLNotification from "application/components/CTLNotification";

function Illustration(props) {
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();
  const { activePage } = controller;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "SignIn",
  });
  const [captchaSuccess, setCaptchaSuccess] = useState(false);
  const [productName, setProductName] = useState("");

  const isRole = ["ROLE_ACD_SUPERVISOR", "ROLE_TEAMLEADER"];
  const isDispatcherRole = ["ROLE_RAILWAY_DISPATCHER", "ROLE_MCX_DISPATCHER"];
  const agentStatusRoles = [
    "ROLE_AGENT",
    "ROLE_ACD_SUPERVISOR",
    "ROLE_OPERATOR_SUPERVISOR",
    "ROLE_TEAMLEADER",
    "ROLE_OCSUPERVISOR",
    "ROLE_OCADMIN",
    "ROLE_MANAGER",
    "ROLE_DISPATCHER",
    "ROLE_RAILWAY_DISPATCHER",
    "ROLE_MCX_DISPATCHER",
  ];

  const shouldRouteToHomePage = (userRoles = []) => {
    if (userRoles.some((role) => isRole.includes(role))) {
      return true;
    }
    // ROLE_USER only routes to home-page if no other roles are assigned
    if (userRoles.length === 1 && userRoles.includes("ROLE_USER")) {
      return true;
    }
    return false;
  };

  const hasDispatcherRole = (userRoles = []) => {
    return userRoles.some((role) => isDispatcherRole.includes(role));
  };

  const needsAgentStatus = (userRoles = []) => {
    return userRoles.some((role) => agentStatusRoles.includes(role));
  };

  useEffect(() => {
    AuthenticationService.logout();
    setAuthentication(dispatch, false);
  }, []);

  useEffect(() => {
    AuthenticationService.getProductName()
      .then((response) => {
        const name = response?.data?.data;
        if (name) {
          setProductName(name);
        }
      })
      .catch(() => {
        setProductName("");
      });
  }, []);

  async function login() {
    setLoading(true);
    setUserName(dispatch, username);
    setPassWord(dispatch, password);
    try {
      const encryptedUsername = encryptData(username, window.secretKey);
      const encryptedPassword = encryptData(password, window.secretKey);

      const response = await AuthenticationService.login(
        encryptedUsername,
        encryptedPassword,
      );
      setLoading(false);
      AuthenticationService.storeAuthenticationDetails(
        response.data.data.sipPhoneButton,
        response.data.data.token,
        response.data.data.refreshToken,
        response.data.data.userName,
        response.data.data.fullName,
        response.data.data.roles,
        response.data.data.menu,
        response.data.data.coralApplication,
        {
          extensionName: response.data.data.extensionName,
          extension: response.data.data.extension,
          sipPassword: response.data.data.sipPassword,
        },
        response.data.data.rluCode,
        response.data.data.rluIp,
        response.data.data.pwdComplication,
        response.data.data.userId,
        response.data.data?.ringAnswerCount,
        response.data.data?.publicEmergencyNumber,
        response.data.data?.broadcastNumber,
        response.data.data?.wssDomain,
        response.data.data?.mapUrl,
        response.data.data?.mapCenter,
        response.data.data?.mapDefaultZoom,
        response.data.data?.mapMinZoom,
        response.data.data?.mapMaxZoom,
        response.data.data?.xmPPPrimaryDomain,
        response.data.data?.departmentList,
        response.data.data?.idealTimeout,
        response.data.data?.description,
        response.data.data?.appMode,
        response.data.data?.enableTTS,
        response.data.data?.disableContextBasedGui,
        response.data.data?.turnServerUrl,
        response.data.data?.turnUserName,
        response.data.data?.turnPassword,
        {
          currencySymbol:      response.data.data?.currencySymbol,
          decimalSeparator:    response.data.data?.decimalSeparator,
          groupingSeparator:   response.data.data?.groupingSeparator,
          groupingStyle:       response.data.data?.groupingStyle,
          taxLabel:            response.data.data?.taxLabel,
          taxRounding:         response.data.data?.taxRounding,
          roundingMode:        response.data.data?.roundingMode,
          amountInWords:       response.data.data?.amountInWords,
          currencyWord:        response.data.data?.currencyWord,
          currencyFractionWord: response.data.data?.currencyFractionWord,
        },
      );

      if (props.login) {
        props.onLogin(true);
      }
      const rawRoles = response?.data?.data?.roles || [];
      const userRoles = Array.isArray(rawRoles) ? rawRoles : String(rawRoles).split(",");
      const contextBasedGuiDisabled = AuthenticationService.isContextBasedGuiDisabled();

      if (!contextBasedGuiDisabled) {
        // Navigate ROLE_ADMIN and PBX viewer roles to Admin GUI (before React state updates)
        if (
          userRoles.includes("ROLE_ADMIN") ||
          userRoles.includes("ROLE_PBX_VIEWER") ||
          userRoles.includes("ROLE_PBX_SYSTEM_VIEWER") ||
          userRoles.includes("ROLE_ALLPBXMENUVIEWER") ||
          userRoles.includes("ROLE_ALLPBXSYSTEMVIEWER")
        ) {
          window.location.href = window.location.origin + "/admin-gui/";
          return;
        }

        // Navigate allowed roles to home-page (before React state updates)
        if (shouldRouteToHomePage(userRoles)) {
          // Post agent login status before redirecting (non-blocking)
          if (needsAgentStatus(userRoles)) {
            try {
              await application.post("/agentStatus/create", { status: "Login" });
            } catch (e) {
              console.warn("agentStatus/create failed before home-page redirect", e);
            }
          }
          window.location.href = "/home-page/";
          return;
        }
      }

      if (needsAgentStatus(userRoles)) {
        const payload = { status: "Login" };
        try {
          await application.post("/agentStatus/create", payload);
          if (AuthenticationService.allowRailwayDispatcher()) {
            application.post("/agentStatus/create", { status: "Start Call" });
          }
        } catch (agentStatusError) {
          console.warn("agentStatus update failed, continuing login", agentStatusError);
        }
      }

      // if (hasAllowedRole(userRoles)) {
      //   window.location.href = "/home-page";
      //   return;
      // }

      if (!contextBasedGuiDisabled && hasDispatcherRole(userRoles)) {
        window.location.href = "/dispatcher";
        return;
      }

      activePage.key = "sign.in";
      activePage.authenticationStatus = true;
      externalCallServer.defaults.baseURL =
        AuthenticationService.getRLUIpAddress();

      setActivePage(dispatch, activePage);
      setAuthentication(dispatch, true);
      setLayout(dispatch, "dashboard");
      navigate("/app/home", { replace: true });
      
    } catch (error) {
      setLoading(false);
      setNotify({
        isOpen: true,
        pagename: "SignIn",
        status:
          error.response?.status === 400
            ? error.response.data.message +
              " " +
              error.response.data.messageDetail
            : error.message,
        type: "error",
      });
      if (error.response?.status === 503) {
        setNotify({
          pagename: "SignIn",
          isOpen: true,
          message: "Service Unavailable. Please try again later...",
          type: "warning",
        });
      }

      if (
        error.response?.status === 400 &&
        error.response.data.data?.requiredChangePassword === true
      ) {
        AuthenticationService.setChangePasswordRequired("true");
        setTimeout(() => {
          navigate("/app/ChangerequiredPassword");
        }, 3000);
      } else {
        console.error("Error:", error);
      }
    }
  }

  const [showPassword, setShowPassword] = useState(false);

  // Visibility & VisibilityOff Password
  const handleShowPassword = () => {
    setShowPassword(true);
  };

  const handleHidePassword = () => {
    setShowPassword(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      login();
    }
  };

  // Function to encrypt data with a random IV
  const encryptData = (data, secretKey) => {
    const iv = CryptoJS.lib.WordArray.random(16); // Generate a random IV
    const ciphertext = CryptoJS.AES.encrypt(
      data,
      CryptoJS.enc.Utf8.parse(secretKey),
      {
        iv: iv,
        mode: CryptoJS.mode.CFB,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    return iv.concat(ciphertext.ciphertext).toString(CryptoJS.enc.Base64);
  };

  const signInButtonColor = window.captcha
    ? captchaSuccess
      ? "info"
      : "error"
    : "info";
  const ButtonDisable = window.captcha ? !captchaSuccess : false;

  return (
    <IllustrationLayout
      title={`${productName}`}
      description="Enter your username and password to login"
    >
      <MDBox component="form" role="form">
        <MDBox mb={2}>
          <MDInput
            type="text"
            label="User Name"
            fullWidth
            onChange={(e) => setUsername(e.target.value)}
            onKeyPress={handleKeyPress}
            autoComplete="off"
          />
        </MDBox>
        <MDBox mb={2} display="flex" alignItems="center">
          <MDInput
            type={showPassword ? "text" : "password"}
            label="Password"
            fullWidth
            value={password}
            autoComplete="off"
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onMouseDown={handleShowPassword}
                    onMouseUp={handleHidePassword}
                    edge="end"
                    style={{ margin: "1px" }}
                    color="info"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onKeyPress={handleKeyPress}
          />
        </MDBox>
        {window.captcha && (
          <MDBox sx={{ display: "flex", justifyContent: "center" }}>
            <Captcha
              onKeyPress={handleKeyPress}
              value={captchaSuccess}
              onChange={(value) => setCaptchaSuccess(value)}
              onRefresh={() => console.log("!1111")}
            />
          </MDBox>
        )}
        <MDBox mt={2} mb={1} sx={{ position: "relative" }}>
          <MDButton
            disabled={loading}
            variant="gradient"
            color={signInButtonColor}
            size="large"
            fullWidth
            onClick={login}
          >
            Login
          </MDButton>
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                color: "info",
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
        </MDBox>
        <MDBox
          mb={2}
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <MDTypography variant="caption">
            {productName + " " + process.env.REACT_APP_VERSION}
          </MDTypography>
        </MDBox>
      </MDBox>
      <CTLNotification notify={notify} setNotify={setNotify} />
    </IllustrationLayout>
  );
}

export default Illustration;
